<template>
    <div style="width: 100%; min-height: 500px; height: 100%; position: relative;" :key="key">
        <vue-flow v-model="elements" :default-zoom="1" :min-zoom="0.15" :max-zoom="4" :fit-view-on-init="true"
            :node-dimensions="nodeDimensions" @nodeMouseEnter="showTooltip" @nodeMouseLeave="hideTooltip" @nodeClick="onNodeClick">
            <template #node-default="{ label, data }">
                <Handle type="target" :position="Position.Left" />
                <div v-if="data?.status?.toLowerCase() === 'failed' && !data?.markedSuccess" class="node-floating-icon failed">
                    <i class="fa-solid fa-triangle-exclamation"></i>
                </div>
                <div class="vue-flow__node-label">
                    <div class="d-flex align-items-center justify-content-center gap-2">
                        <i v-if="data?.skip" class="fa-solid fa-forward fs-3 text-primary"></i>
                        <i v-if="data?.markedSuccess" class="fa-solid fa-check-circle fs-3" style="color: #006f41;"></i>
                        <div class="font-monospace fw-bold">
                            {{ label }}
                        </div>
                    </div>
                </div>
                <Handle type="source" :position="Position.Right" />
            </template>
            <background pattern-color="#aaa" gap="16" />
            <controls />
            <mini-map />
        </vue-flow>
    </div>
</template>

<script>
import { VueFlow, Background, Controls, MiniMap, Handle, Position } from '@vue-flow/core';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import YAML from 'yaml';

// node alignment configuration
const NODE_WIDTH = 250;
const NODE_HEIGHT = 50;
const X_GAP = 80; // Gap between sequential nodes (now horizontal)
const Y_GAP = 60; // Gap between parallel nodes (now vertical)
const CHAR_WIDTH = 12; // Approximate pixels per character
const MIN_WIDTH = 100;
const PADDING = 0; // Left + Right padding inside the box

export default {
    name: 'PipelineFlow',
    components: {
        VueFlow,
        Background,
        Controls,
        MiniMap,
        Handle,
    },
    props: {
        pipelineExecutionDetails: {
            type: Object,
            default: () => null
        },
        pipelineJobConfig: {
            type: Object,
            default: () => null
        },
        jobs: {
            type: Array,
            default: () => []
        },
        key: {
            type: String,
            default: () => 'pipeline-graph'
        }
    },
    data() {
        return {
            elements: [],
            nodeDimensions: { width: NODE_WIDTH, height: NODE_HEIGHT },
            statusColorClass: {
                'running': 'running-node',
                'succeeded': 'succeeded-node',
                'failed': 'failed-node',
                'cancelled': 'cancelled-node',
                'submitted': 'submitted-node',
                'not_started': 'waiting-node',
                'waiting': 'waiting-node' // fallback
            },

            idCounter: 0, // for unique node id generation
            Position: Position,
        };
    },
    computed: {
        pipelineConfig() {
            if (!this.pipelineJobConfig?.jobDetails?.system_files) return null;
            const configFile = this.pipelineJobConfig.jobDetails.system_files.find(
                f => f.relative_path === 'config.yaml'
            );
            const varsFile = this.pipelineJobConfig.jobDetails.system_files.find(
                f => f.relative_path === 'vars.yaml'
            );
            if (!configFile?.content) return null;
            try {
                // merging is required, since the config file may refer anchors defined in vars file.
                // yaml parser does not support a case where anchors are used but not defined.
                const mergedFile = (varsFile?.content || '') + '\n' + (configFile?.content || '');

                // parseAllDocuments returns an iterable of Document objects
                const documents = YAML.parseAllDocuments(mergedFile);

                for (const doc of documents) {
                    // Convert the YAML document to a plain JavaScript object
                    const parsed = doc.toJS();

                    if (!parsed) continue;

                    // Normalize root: return the first one that matches your criteria
                    if (parsed.in_sequence) {
                        return { in_sequence: parsed.in_sequence };
                    }
                    if (parsed.in_parallel) {
                        return { in_parallel: parsed.in_parallel };
                    }
                }
                return null;
            } catch (e) {
                console.error('YAML parse error', e);
                return null;
            }
        }
    },
    mounted() {
        if (this.pipelineConfig) {
            this.generateFlow();
        }
    },
    methods: {
        nextId(name = 'job') {
            return `${name}-${++this.idCounter}`;
        },
        getJobKind(jobName) {
            const job = this.jobs?.find(job => job.jobDetails?.name === jobName);
            return job?.jobDetails?.kind || "N/A";
        },
        showTooltip(event) {
            const { id } = event.node;
            const mouseEvent = event.event;
            const node = this.elements.find(el => el.id === id && !el.source);

            if (!node) return;

            const tooltip = document.createElement('div');
            tooltip.innerHTML = `
                <div style='font-size: 12px; padding: 3px;'>
                    <div><strong>Name: </strong> ${node.data.name} </div>
                    <div><strong>Kind: </strong> ${node.data.kind || 'N/A'} </div>
                    <div><strong>Status: </strong> ${node.data.status.toUpperCase() || 'N/A'} </div>
                    ${node.data.skip ? '<div><strong>Skip: </strong> Yes </div>' : ''}
                    ${node.data.record_count ? `<div><strong>Record Count: </strong> ${node.data.record_count || 'N/A'} </div>` : ''}
                </div>
            `;

            tooltip.style.left = mouseEvent.pageX + 5 + 'px';
            tooltip.style.top = mouseEvent.pageY + 5 + 'px';
            tooltip.classList.add('vueflow-tooltip');

            this.hideTooltip(); // Clear old ones
            document.body.appendChild(tooltip);
        },
        hideTooltip() {
            const existingTooltip = document.querySelector('.vueflow-tooltip');
            if (existingTooltip) {
                existingTooltip.remove();
            }
        },
        onNodeClick(event) {
            this.$emit('nodeClick', event.node.data.name);
        },

        // Calculate node width based on label
        calculateNodeWidth(label) {
            const textWidth = label.length * CHAR_WIDTH;
            return Math.max(MIN_WIDTH, textWidth + PADDING);
        },

        generateFlow() {
            // Start with false as the root level isn't skipped unless defined in the YAML
            const layoutTree = this.getDimensions(this.pipelineConfig, false);
            const { nodes, edges } = this.buildFlow(layoutTree, 0, 0, layoutTree.width, layoutTree.height);
            this.elements = [...nodes, ...edges];
        },

        getDimensions(node, inheritedSkip = false) {
            // Current node is skipped if it defines skip: true OR if it inherited a skip from a parent
            const isSkipped = node.skip === true || inheritedSkip === true;

            if (node.job_name) {
                const dynamicWidth = this.calculateNodeWidth(node.job_name);
                return {
                    ...node,
                    width: dynamicWidth,
                    height: 50,
                    skip: isSkipped // Leaf node now knows if it or its parent was skipped
                };
            }

            if (node.in_sequence) {
                // Pass the current isSkipped state down to all children in the sequence
                const children = node.in_sequence.map(child => this.getDimensions(child, isSkipped));
                return {
                    in_sequence: children,
                    width: children.reduce((sum, c) => sum + c.width, 0) + (children.length - 1) * X_GAP,
                    height: Math.max(...children.map(c => c.height)),
                    skip: isSkipped
                };
            }

            if (node.in_parallel) {
                // Pass the current isSkipped state down to all children in the parallel block
                const children = node.in_parallel.map(child => this.getDimensions(child, isSkipped));
                return {
                    in_parallel: children,
                    width: Math.max(...children.map(c => c.width)),
                    height: children.reduce((sum, c) => sum + c.height, 0) + (children.length - 1) * Y_GAP,
                    skip: isSkipped
                };
            }
        },

        /**
         * @param node The layout node
         * @param startX Current X position
         * @param startY Current Y position
         * @param containerW The width of the "cell" this node lives in
         * @param containerH The height of the "cell" this node lives in
         */
        buildFlow(node, startX, startY, containerW, containerH) {
            const xOffset = (containerW - node.width) / 2;
            const yOffset = (containerH - node.height) / 2;

            if (node.job_name) {
                const id = this.nextId(node.job_name);
                let status = "";
                if (this.pipelineExecutionDetails?.executionDetails) {
                    status = this.pipelineExecutionDetails?.executionDetails[node.job_name]?.status || "WAITING";
                }

                // marked success for displaying jobs that have been marked as successful
                const markedSuccess = this.pipelineExecutionDetails?.executionDetails?.[node.job_name]?.marked_success === true ? true : false;

                // status class for displaying the status of the job
                let statusClass = this.statusColorClass[status.toLowerCase()] || 'waiting-node';
                if (markedSuccess) statusClass = 'succeeded-node-marked';

                // record count for displaying the record count of the job
                const recordCount = this.pipelineExecutionDetails?.executionDetails?.[node.job_name]?.record_count || null;

                return {
                    nodes: [{
                        id,
                        type: 'default', // Changed from 'custom' to 'default'
                        position: { x: startX + xOffset, y: startY + yOffset },
                        label: node.job_name, // Vue Flow uses this for the text display
                        sourcePosition: 'right', // Forces handle to Right
                        targetPosition: 'left',  // Forces handle to Left
                        style: {
                            width: `${node.width + (node.skip ? 28 : 0) + (markedSuccess ? 28 : 0)}px`, // calculated node width + icon widths if any (28px for fs-3 bootstrap font size)
                            height: '50px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        },
                        // Applying the status class directly to the node
                        class: statusClass + (node.skip ? ' skipped' : ''),
                        data: {
                            name: node.job_name,
                            kind: this.getJobKind(node.job_name),
                            status: status,
                            record_count: recordCount,
                            skip: node.skip,
                            markedSuccess: markedSuccess
                        }
                    }],
                    edges: [],
                    entryIds: [id],
                    exitIds: [id]
                };
            }

            if (node.in_sequence) {
                let currentX = startX;
                let nodes = [];
                let edges = [];
                let entryIds = [];
                let prevExitIds = [];

                node.in_sequence.forEach((child, index) => {
                    const result = this.buildFlow(child, currentX, startY, child.width, containerH);
                    nodes.push(...result.nodes);
                    edges.push(...result.edges);
                    if (index === 0) entryIds = result.entryIds;

                    prevExitIds.forEach(src =>
                        result.entryIds.forEach(tgt =>
                            edges.push({
                                id: `e-${src}-${tgt}`,
                                source: src,
                                target: tgt,
                                type: 'smoothstep',
                                animated: true,
                                // This ensures the line connects Right End to Left End
                                style: { stroke: '#000000', strokeWidth: 2 }
                            })
                        )
                    );
                    prevExitIds = result.exitIds;
                    currentX += child.width + X_GAP;
                });
                return { nodes, edges, entryIds, exitIds: prevExitIds };
            }
            // ... rest of in_parallel logic
            if (node.in_parallel) {
                let currentY = startY;
                let nodes = [];
                let edges = [];
                let entryIds = [];
                let exitIds = [];

                node.in_parallel.forEach(child => {
                    // Each child in parallel is given the FULL width of the parallel 
                    // block so they can center themselves horizontally
                    const result = this.buildFlow(child, startX, currentY, containerW, child.height);

                    nodes.push(...result.nodes);
                    edges.push(...result.edges);
                    entryIds.push(...result.entryIds);
                    exitIds.push(...result.exitIds);
                    currentY += child.height + Y_GAP;
                });
                return { nodes, edges, entryIds, exitIds };
            }
        }
    },
    watch: {
        pipelineExecutionDetails: {
            handler(newVal) {
                if (newVal && this.pipelineConfig) {
                    this.idCounter = 0;
                    this.generateFlow();
                }
            }
        },
        jobs: {
            handler(newVal) {
                if (newVal) {
                    this.idCounter = 0;
                    this.generateFlow();
                }
            }
        }
    },
    emits: ['nodeClick'],
};
</script>

<style>
.vue-flow {
    width: 100%;
    height: 100%;
}

.vue-flow__node {
    cursor: pointer;
}

.vue-flow__edge path {
    stroke: #ccc;
    stroke-width: 2px;
}

.vueflow-tooltip {
    position: absolute;
    pointer-events: none;
    border-radius: 4px;
    z-index: 1000;
    background-color: #6E84A3;
    color: #fff;
    visibility: visible !important;
}

/* Remove or comment out .custom-node */
.vue-flow__handle {
    opacity: 0 !important;
    /* Optional: Ensure they don't take up space if needed, 
       though opacity 0 is usually enough to 'hide' them */
    background: transparent !important;
    border: none !important;
}

.vue-flow__node-default {
    border-radius: 5px;
    border: 1px solid #000000;
    color: #000000;
    font-size: 14px;
    font-weight: 500;
}

/* Update your status classes to apply to the default node background */
.vue-flow__node-default.running-node {
    background-color: #ffd118 !important;
    /* color: #ffffff !important; */
    border: 2px solid black !important;

    /* IMPORTANT: do NOT change positioning */
    overflow: hidden;
    animation: concourseGaps 2s infinite ease-out;
}

/* Stripe overlay */
.vue-flow__node-default.running-node::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    pointer-events: none;

    background-image: repeating-linear-gradient(
        135deg,
        /* The "Darker" Stripe (6px) */
        rgba(0, 0, 0, 0.05) 0px,
        rgba(0, 0, 0, 0.05) 6px,
        
        /* The Gap (10px) - Keeping the original 0.05 alpha weight */
        rgba(0, 0, 0, 0.25) 6px,
        rgba(0, 0, 0, 0.25) 16px
    );
    background-size: 68px 68px;
    animation: diagonalFlow 4s linear infinite;
    opacity: 0.35;
}

/* Sharp Rectangular Ripples */
@keyframes concourseGaps {
    0% {
        box-shadow:
            /* Line 1: Visible */
            0 0 0 2px rgba(255, 215, 0, 0.8),
            /* THE GAP: A transparent spread of 10px */
            0 0 0 13px rgba(255, 215, 0, 0),
            /* Line 2: Visible further out */
            0 0 0 15px rgba(255, 215, 0, 0.4),
            /* THE GAP: Another transparent spread */
            0 0 0 20px rgba(255, 215, 0, 0);
    }
    100% {
        box-shadow:
            /* Line 1 moves to where Line 2 was */
            0 0 0 15px rgba(255, 215, 0, 0.4),
            0 0 0 20px rgba(255, 215, 0, 0),
            /* Line 2 fades out further away */
            0 0 0 22px rgba(255, 215, 0, 0),
            0 0 0 30px rgba(255, 215, 0, 0);
    }
}

@keyframes diagonalFlow {
    from { background-position: 0px 0px; }
    to { background-position: 68px 68px; }
}

.vue-flow__node-default.succeeded-node {
    background-color: var(--bs-success) !important;
    border: none !important;
}

.vue-flow__node-default.succeeded-node-marked {
    background-color: #97ffd4 !important;
    color: #006f41 !important;
    border: 2px solid #006f41 !important;
}

.vue-flow__node-default.failed-node {
    background-color: #e63757 !important;
    color: white !important;
    border: none !important;

    box-shadow: 
        0 0 0 6px rgb(255, 255, 255),  /* Inner light red band */
        0 0 0 14px rgba(230, 55, 87, 0.541) !important; /* Outer very faint red band */
}

.vue-flow__node-default.submitted-node {
    background-color: #acb4c1 !important;
    border: none !important;
}

.vue-flow__node-default.cancelled-node {
    background-color: #915b1d !important;
    color: white !important;
    border: none !important;
}

.vue-flow__node-default.waiting-node {
    background-color: #EDF2F9 !important;
}

.vue-flow__node-default.waiting-node.skipped {
    border: 2px solid var(--bs-primary) !important;
    background-color: #d2e5ff !important;
}

.node-floating-icon {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    /* -40px clears the 14px shaded area comfortably */
    top: -40px; 
    font-size: 24px;
    z-index: 10;
    line-height: 1;
    color: #e63757;
}

/* Ensure edges are visible and dark */
.vue-flow__edge-path {
    stroke: #000000 !important;
    stroke-width: 2px;
}
</style>